<h1 class="main-title">MZIC Platform Terms of Use</h1>

<h2 class="section-heading">1. Who we are and processing roles</h2>
<p class="text-content">
  <strong>Controller:</strong>
  MZIC BRASIL S.A., CNPJ 60.978.531/0001-85, headquartered at Av. Jorge João
  Saad, 305, Room 25, São Paulo/SP, ZIP 05618-000.
</p>

<p class="text-content">
  These Terms govern the access and use of MZIC's electronic participatory
  investment platform by users interested in participating in investment
  opportunities in Receivables Certificates ("RCs") offered in the
  "crowdfunding" modality, under the terms of CVM Resolution 88.
</p>

<h2 class="section-heading">2. Defined terms</h2>
<p class="text-content">
  <strong>"Receivables Certificates (RCs)":</strong>
  These are securitization securities, issued under the terms of Law 14.430,
  backed by credit rights arising from the exploitation of musical works.
</p>
<p class="text-content">
  <strong>"CVM":</strong>
  The Securities and Exchange Commission, federal authority responsible for
  regulating and overseeing the securities market in Brazil.
</p>
<p class="text-content">
  <strong>"Dashboard":</strong>
  Internal digital environment of the Platform for monitoring operations,
  positions and earnings.
</p>
<p class="text-content">
  <strong>"Investor":</strong>
  User who uses the Platform to acquire Receivables Certificates.
</p>
<p class="text-content">
  <strong>"Law 14.430":</strong>
  Federal Law No. 14.430, of August 3, 2022, as in effect.
</p>
<p class="text-content">
  <strong>"LGPD":</strong>
  Federal Law No. 13.709, of August 14, 2018, as in effect (General Data
  Protection Law).
</p>
<p class="text-content">
  <strong>"Platform":</strong>
  The electronic participatory investment platform of MZIC BRASIL S.A., duly
  authorized by CVM under the terms of Declaratory Act No. 23.964, of September
  29, 2025.
</p>
<p class="text-content">
  <strong>"CVM Resolution 88":</strong>
  CVM Resolution No. 88, of April 27, 2022, as in effect.
</p>
<p class="text-content">
  <strong>"Terms":</strong>
  These "Terms of Use – Application Users (Investors)".
</p>
<p class="text-content">
  <strong>"User":</strong>
  Individual or legal entity registered on the Platform, including their legal
  representatives, as applicable.
</p>
<p class="text-content">
  <strong>"Smart Wallet/Smart Digital Account":</strong>
  technological credential associated with the User's account for
  authentication, transaction signing and key storage, which may rely on
  on-chain infrastructure.
</p>

<h2 class="section-heading">3. Acceptance of Terms</h2>
<p class="text-content">
  Upon registration, the User declares having read, understood and fully
  accepted these Terms. If you do not agree, you should not use the Platform.
</p>

<h2 class="section-heading">4. Registration, eligibility and compliance</h2>

<h3 class="subsection-heading">4.1 Minimum eligibility</h3>
<p class="text-content">
  <strong>(i) Individual (PF):</strong>
  be over 18 years old and provide, at least, full name, email, phone, date of
  birth and CPF;
  <strong>(ii) Legal entity (PJ):</strong>
  provide company name/trade name, CNPJ, contact email and phone, in addition to
  the legal representative's data (name, CPF and date of birth).
</p>

<h3 class="subsection-heading">
  4.2 Compliance requirements and additional documentation
</h3>
<p class="text-content">
  The Platform may request additional documents (proof documents, suitability
  questionnaire, etc.) according to CVM rules and other authorities. If the User
  makes investment(s) that, individually or jointly considered, exceed(s) the
  limit of R$ 20,000.00 (twenty thousand reais), the submission of additional
  documentation will be required, including proof of income and proof of address
  (for PF) or corporate documents and representative qualification (for PJ),
  without prejudice to other requests. The Platform may request other documents
  or information whenever necessary to meet legal, regulatory, fraud prevention
  or security requirements. Failure to comply with requirements may result in
  suspension or blocking of the User's account.
</p>

<h3 class="subsection-heading">4.3 KYC/AML and sensitive data</h3>
<p class="text-content">
  The User authorizes identity validation (includes document and
  selfie/biometrics) by specialized KYC/KYB/AML providers. Lack of verification
  may prevent investments and/or withdrawals.
</p>

<h3 class="subsection-heading">4.4 Registration update</h3>
<p class="text-content">
  The User agrees to keep their data accurate and up to date.
</p>

<h2 class="section-heading">5. Regulatory statements and nature of services</h2>
<p class="text-content">
  CVM does not guarantee the veracity of information, the quality of assets or
  the compliance of offers. MZIC does not provide investment advice, does not
  recommend assets, does not manage third-party resources and does not act as a
  financial institution. The decision to invest is exclusively the User's, who
  must evaluate the offer documents.
</p>

<h2 class="section-heading">6. Risk warnings</h2>
<p class="text-content">
  The Investor acknowledges that:
  <strong>(i)</strong>
  there is risk of total or partial loss of capital;
  <strong>(ii)</strong>
  there is no guarantee of liquidity or profitability;
  <strong>(iii)</strong>
  the performance of securities depends on market factors and the performance of
  credit rights; and
  <strong>(iv)</strong>
  it is their responsibility to read the offer documents, including risk
  warnings and essential information.
</p>

<h2 class="section-heading">7. Investor responsibilities</h2>
<p class="text-content">
  The Investor is responsible for the veracity of the information provided,
  compliance with applicable regulations and safeguarding their credentials. Use
  of the Platform for illegal purposes or in disagreement with these Terms may
  result in suspension or cancellation of the account, without prejudice to
  applicable legal measures.
</p>

<h2 class="section-heading">8. Account, security and Smart Wallet</h2>

<h3 class="subsection-heading">8.1 Account creation and security</h3>
<p class="text-content">
  The User must protect their credentials and devices. Any suspicious activity
  must be reported immediately.
</p>

<h3 class="subsection-heading">8.2 Smart Wallet/on-chain infrastructure</h3>
<p class="text-content">
  The Smart Wallet is a technological means to authenticate and sign operations
  on the Platform, and may use on-chain addresses. On-chain addresses and
  blockchain records have a public and immutable nature.
</p>

<h3 class="subsection-heading">8.3 Recovery and support</h3>
<p class="text-content">
  MZIC may offer access recovery/contingency mechanisms (e.g.: social keys,
  custody providers), without obligation to store User passwords.
</p>

<h2 class="section-heading">9. Scope of Platform services</h2>
<p class="text-content">
  The Platform may offer, in accordance with applicable regulations, investment
  opportunities in RCs backed by credit rights from the music industry. The
  Platform will provide means for monitoring operations and receiving earnings,
  subject to the specific conditions of each Offer.
</p>

<h2 class="section-heading">10. Privacy, cookies and analytics</h2>

<h3 class="subsection-heading">10.1 Privacy Policy</h3>
<p class="text-content">
  The processing of personal data follows the MZIC Platform Privacy Policy,
  available at /privacy.
</p>

<h3 class="subsection-heading">10.2 Cookies and preferences</h3>
<p class="text-content">
  We use cookies and similar technologies for authentication, security,
  preferences and analytics.
</p>

<h3 class="subsection-heading">10.3 PostHog (usage and session analytics)</h3>
<p class="text-content">
  We use PostHog for usage metrics and, when enabled, session recording/heatmaps
  exclusively for UX diagnostics.
</p>

<h2 class="section-heading">11. Essential third-party service providers</h2>
<p class="text-content">
  By using the Platform, you agree that certain services are provided by third
  parties, whose terms and policies also apply, as applicable:
</p>
<ul class="content-list">
  <li>
    <strong>(a)</strong>
    Plaid Technologies, Inc. – KYC/AML.
  </li>
  <li>
    <strong>(b)</strong>
    Banco Transfero – account/payments and settlement.
  </li>
  <li>
    <strong>(c)</strong>
    PostHog – analytics (events and, if enabled, session).
  </li>
</ul>
<p class="text-content">
  Other providers may be used for cloud computing, fraud prevention, messaging
  and support.
</p>

<h2 class="section-heading">12. Intellectual property</h2>
<p class="text-content">
  All rights related to the Platform, brands, codes, interfaces and content are
  owned by MZIC or its licensors. It is prohibited to copy, modify, distribute,
  reverse engineer or create derivative works without express authorization.
</p>

<h2 class="section-heading">13. Taxation</h2>
<p class="text-content">
  The Investor is responsible for complying with their tax obligations
  (including declarations and payments). MZIC may make legal withholdings when
  applicable and provide income reports, if applicable.
</p>

<h2 class="section-heading">14. Communications and electronic acceptance</h2>
<p class="text-content">
  The User consents to receive communications by electronic means (email,
  app/web notifications) and recognizes as valid the electronic acceptance in
  Platform flows, whose agreement logs may be used as evidence.
</p>

<h2 class="section-heading">15. Modifications to these Terms</h2>
<p class="text-content">
  MZIC may modify these Terms at any time, upon prior communication to the User
  by email and/or through the Platform itself. Continued use after communication
  implies acceptance of the current version.
</p>

<h2 class="section-heading">16. Applicable law and jurisdiction</h2>
<p class="text-content">
  Brazilian law applies. The Court of the District of São Paulo/SP is elected,
  to the exclusion of any other, to resolve any disputes related to these Terms.
</p>

<h2 class="section-heading">17. Legal contacts and channels</h2>
<p class="text-content">
  Questions about these Terms and/or complaints can be sent to
  <strong>legal&#64;mzic.io</strong>
  .
</p>

<p class="text-content">
  <strong>Effectiveness:</strong>
  these Terms take effect on their publication date and remain valid while the
  User maintains an account or uses the Platform.
</p>
